import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';
import { Shield } from 'lucide-react';

export const Privacy = () => {
  return (
    <Layout>
      <SeoHead
        title="Privacy Policy | EnergiPro Solutions"
        description="EnergiPro Solutions privacy policy: data collection, usage, cookies, third-party sharing, and your rights under applicable data protection regulations."
        canonicalPath="/privacy"
      />
      <div className="bg-slate-900 pt-80 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Shield className="text-orange-600" size={48} />
            <h1 className="text-4xl md:text-5xl font-bold text-white">PRIVACY POLICY</h1>
          </div>
          <p className="text-xl text-slate-300 max-w-3xl leading-relaxed">
            How EnergiPro Solutions collects, uses, and protects your information.
          </p>
        </div>
      </div>

      <Section>
        <div className="max-w-4xl mx-auto prose prose-slate prose-lg">
          <p className="text-sm text-slate-500 mb-8">Last updated: April 25, 2026</p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">1. Information We Collect</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            EnergiPro Solutions (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects personal information that you voluntarily provide when you submit inquiries through our website contact forms, request quotes, or engage in commercial correspondence. This may include:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>Full name and professional title</li>
            <li>Company name and business address</li>
            <li>Email address and phone number</li>
            <li>Inquiry type and project details</li>
            <li>Any additional information you choose to provide in messages</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">2. How We Use Your Information</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We use the information collected to:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>Respond to commercial and operational inquiries</li>
            <li>Process prequalification and vendor registration requests</li>
            <li>Provide requested documentation and capability statements</li>
            <li>Coordinate operational logistics and project requirements</li>
            <li>Improve our services and website experience</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">3. Data Protection</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All data transmissions are encrypted, and access to personal data is restricted to authorized personnel who require it for legitimate business purposes.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">4. Data Sharing</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            We do not sell, trade, or rent your personal information to third parties. We may share information with trusted partners and service providers who assist us in operating our business, provided they agree to maintain confidentiality. We may also disclose information when required by law or to protect our legal rights.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">5. Data Retention</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            We retain personal information for as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements. Commercial inquiry data is retained in accordance with applicable record-keeping requirements.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">6. Your Rights</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            You have the right to request access to, correction of, or deletion of your personal information. To exercise these rights, please contact us at <a href="mailto:info@energiprogroup.com" className="text-orange-600 hover:text-orange-700 underline">info@energiprogroup.com</a>. We will respond to all legitimate requests within a reasonable timeframe.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">7. Cookies & Analytics</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            Our website may use essential cookies to ensure proper functionality. We do not use tracking cookies for advertising purposes. Any analytics data collected is aggregated and anonymized.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">8. Contact</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            For any questions regarding this Privacy Policy, please contact us at:<br />
            <strong>Email:</strong> <a href="mailto:info@energiprogroup.com" className="text-orange-600 hover:text-orange-700 underline">info@energiprogroup.com</a><br />
            <strong>Phone:</strong> +966 13 330 8579<br />
            <strong>Address:</strong> Level 21 Al Khobar Gate Tower (Al Shaikh Tower), King Fahad Road, PO Box 32348 Khobar 31952, KSA
          </p>
        </div>
      </Section>
    </Layout>
  );
};
